import i18n from '@/plugins/i18n';

type TranslateFn = (key: string, params?) => string;
type ExistsFn = (key: string) => boolean;

function isLiteralValue(key: string): boolean {
  return !!(key && (key.includes('@') || key.includes('http') || /^\d/.test(key)));
}

/**
 * Small helper that mirrors the old labelMixin.t(label) behaviour.
 * It prefers an injected translate/existence check via params, otherwise falls back
 * to the global i18n instance.
 */
export function useLabel({ t, te }: { t?: TranslateFn; te?: ExistsFn } = {}) {
  // If injected, use them
  if (t && te) {
    function labelT(label: string, params?): string {
      if (isLiteralValue(label)) {
        return label;
      }
      if (te(label)) {
        return t(label, params) as string;
      }
      return '';
    }
    return { t: labelT };
  }

  // Otherwise, use the global i18n
  const globalI18n = i18n.vueI18n?.global;
  if (!globalI18n) {
    return { t: () => '' };
  }

  function labelT(label: string, params?): string {
    if (isLiteralValue(label)) {
      return label;
    }
    if (globalI18n.te?.(label) && globalI18n.t) {
      // Try to read the raw message first and avoid calling the compiler for simple literals
      try {
        const locale =
          (globalI18n.locale && (globalI18n.locale as any).value) ||
          (globalI18n.locale as any) ||
          'en';
        const messages =
          typeof globalI18n.getLocaleMessage === 'function'
            ? globalI18n.getLocaleMessage(locale)
            : (globalI18n.messages && globalI18n.messages[locale]) || {};
        const parts = String(label).split('.');
        let node: any = messages;
        for (const part of parts) {
          if (
            node &&
            typeof node === 'object' &&
            Object.prototype.hasOwnProperty.call(node, part)
          ) {
            node = node[part];
          } else {
            node = null;
            break;
          }
        }
        // If the raw message is a simple string that contains an @ (e.g., an email),
        // return it directly to avoid triggering the message compiler and its linked-format parsing.
        if (typeof node === 'string' && node.includes('@')) {
          return node;
        }
      } catch {
        // ignore and fall back to normal t()
      }

      try {
        return globalI18n.t(label, params || {}) as string;
      } catch (err) {
        // As a last resort, return the raw message if available to avoid throwing
        try {
          const locale =
            (globalI18n.locale && (globalI18n.locale as any).value) ||
            (globalI18n.locale as any) ||
            'en';
          const messages =
            typeof globalI18n.getLocaleMessage === 'function'
              ? globalI18n.getLocaleMessage(locale)
              : (globalI18n.messages && globalI18n.messages[locale]) || {};
          const parts = String(label).split('.');
          let node: any = messages;
          for (const part of parts) {
            if (
              node &&
              typeof node === 'object' &&
              Object.prototype.hasOwnProperty.call(node, part)
            ) {
              node = node[part];
            } else {
              node = null;
              break;
            }
          }
          if (typeof node === 'string') return node;
        } catch {
          // ignore
        }
        window.log?.debug('Message compilation error for', label, '-', err?.message || err);
        return '';
      }
    }
    return '';
  }

  return { t: labelT } as const;
}

export default useLabel;
